import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TaxDocumentStatus } from '../../enums/tax';
import { TaxReturnStatus } from "../../model/tax/tax_return";
import { Checkpoint } from '../../enums/checkpoint';
import { TaxSeasonPayload } from "../../model/season";

export interface StateState {
  taxReturnID: number|null;
  taxYear: number|null;
  season: TaxSeasonPayload|null;
  frontendState: {
    isLoading: boolean;
    error: any;
    returnSigned: boolean; // This can reference frontend logic or in conjunction with backend state
    checkpoint: Checkpoint|null; // A checkpoint occurs when the frontend and backend successfully compare states against each other
  },
  // Note that this is only useful in user flows
  // prior to user signing (completing) their return (which is still 90% of interactions).
  backendState: {
    status: TaxDocumentStatus;
  },
}

export const initialState: StateState = {
  taxReturnID: null,
  taxYear: null,
  season: null,
  frontendState: {
    isLoading: true,
    error: null,
    returnSigned: false,
    checkpoint: null,
  },
  backendState: {
    status: TaxDocumentStatus.AwaitingDocuments,
  }
};

export const stateSlice = createSlice({
  name: "state",
  initialState,
  reducers: {
    fetchState: (state) => {
      state.frontendState.isLoading = true;
    },
    fetchFilingDate: (state) => {
      state.frontendState.isLoading = true;
    },
    fetchBackendState: (state) => {
      state.frontendState.isLoading = true;
    },
    cancelFetchState: (state) => {
      state.frontendState.isLoading = false;
    },
    setTaxYear: (state, action: PayloadAction<number>) => {
      state.taxYear = action.payload;
    },
    fetchStateSuccess: (
      state,
      action: PayloadAction<TaxReturnStatus>) => {
      state.frontendState.isLoading = false;
      state.taxReturnID = action.payload.id;
      const status = action.payload.state;
      state.backendState.status = status;
      state.frontendState.returnSigned = status === TaxDocumentStatus.Complete;
    },
    fetchStateError: (state, action: PayloadAction<string>) => {
      state.frontendState.isLoading = false;
      state.frontendState.error = action.payload;
    },
    fetchFilingDateSuccess: (state, action: PayloadAction<TaxSeasonPayload>) => {
      state.season = action.payload;
      state.frontendState.isLoading = false;
    },
    pollBackendStateChange: (state, action: PayloadAction<TaxDocumentStatus>) => {
      state.frontendState.isLoading = true;
    },
    saveCheckpoint: (state, action: PayloadAction<TaxDocumentStatus>) => {
      if (action.payload == TaxDocumentStatus.Complete) {
        // Once this checkpoint is set, the frontend should no longer depend on backend state,
        // since the backend has marked the tax return as "Complete". Any logic for
        // determining payment/refund is now up to frontend logic.
        state.frontendState.checkpoint = Checkpoint.SignedReturn;
      }
    },
    updatePersistedState: (state) => {
      //state.isLoading = true;
    },
    updatePersistedStateSuccess: (state) => {
      state.frontendState.isLoading = false;
    },
    updatePersistedStateError: (state, action) => {
      state.frontendState.isLoading = false;
      state.frontendState.error = action.payload;
    },
    persistInitialState: (state) => {
      state.frontendState.isLoading = true;
    },
    persistInitialStateSuccess: (state) => {
      state.frontendState.isLoading = false;
    },
    persistInitialStateError: (state, action) => {
      state.frontendState.isLoading = false;
      state.frontendState.error = action.payload;
    },
    signReturn: (state) => {
      state.frontendState.returnSigned = true;
    },
    resetError: (state) => {
      state.frontendState.error = '';
    },
    sendToRouteWithDelay: (state, action: PayloadAction<string>) => {

    },
  },
});

export default stateSlice.reducer;
